import { TabPanel } from "@mui/lab";
import { Button } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import useTabSearchParams from "@entrilia/shared/hooks/useTabSearchParams";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import PageTabs from "../../../common/PageTabs";
import ObjectLayouts from "../layouts/ObjectLayouts";
import ObjectFields from "../object-fields/ObjectFields";
import { useObjectDefinitionContext } from "../ObjectDefinitionContext";
import {
  getInitialObjectDefinitionGeneralForm,
  isObjectDefinitionGeneralFormDirty,
  validateObjectDefinitionGeneralForm,
  type ObjectDefinitionGeneralForm,
} from "./objectDefinitionGeneralState";
import ObjectDetailsGeneral from "./ObjectDetailsGeneral";
import ObjectDetailsTitle from "./ObjectDetailsTitle";

const tabValues = ["general", "fields", "layouts"] as const;

const tabPanelSx = { px: 3, py: 2.5, height: "100%" };

const updateObjectDefinition = withErrorHandling(adminApi.updateObjectDefinition);

const ObjectDetailsPage = () => {
  const { objectDefinition, onUpdateObjectDefinition, hasEditPermissions } = useObjectDefinitionContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  usePageTitle(`${objectDefinition.objectName} | Data Model`);

  const { clientCode } = useClientContext();

  const { selectedTab, onTabChange } = useTabSearchParams(tabValues);

  const [form, setForm] = useState<ObjectDefinitionGeneralForm>(() =>
    getInitialObjectDefinitionGeneralForm(objectDefinition)
  );

  const [saving, setSaving] = useState(false);

  const handleCancel = () => {
    setForm({ name: objectDefinition.objectName, supportedFeatures: objectDefinition.supportedFeatures });
  };

  const handleSave = async () => {
    setSaving(true);
    const [updated, error] = await updateObjectDefinition(objectDefinition.objectType, {
      name: form.name.trim(),
      supportedFeatures: form.supportedFeatures,
    });
    setSaving(false);

    if (error) {
      logError(error, "[ObjectDetailsPage] updateObjectDefinition");
      sendNotificationError(error.message);
      return;
    }

    onUpdateObjectDefinition(updated);
    sendNotification("Object definition saved.");
  };

  const pathToObjectsList = `/${clientCode}/${pageRoutes.settings}/${pageRoutes.dataModel}?tab=objects`;

  const isFormDirty = isObjectDefinitionGeneralFormDirty(form, objectDefinition);

  const validationResult = validateObjectDefinitionGeneralForm(form);

  return (
    <>
      <GeneralPageHeader
        TitleComponent={<ObjectDetailsTitle />}
        showDefaultBackButtonTo={pathToObjectsList}
        hideBorderLine
      >
        {selectedTab === "general" && hasEditPermissions && (
          <>
            <Button variant="text" color="secondary" onClick={handleCancel} disabled={saving || !isFormDirty}>
              Cancel
            </Button>
            <Button
              variant="contained"
              loading={saving}
              disabled={!isFormDirty || !validationResult.isValid}
              onClick={handleSave}
            >
              Save
            </Button>
          </>
        )}
      </GeneralPageHeader>
      <PageTabs tabs={tabValues} labels={["General", "Fields", "Layouts"]} value={selectedTab} onChange={onTabChange}>
        <TabPanel value="general" sx={tabPanelSx}>
          <ObjectDetailsGeneral form={form} onFormChange={setForm} validationResult={validationResult} />
        </TabPanel>
        <TabPanel value="fields" sx={{ ...tabPanelSx, overflowY: "hidden" }}>
          <ObjectFields />
        </TabPanel>
        <TabPanel value="layouts" sx={tabPanelSx}>
          <ObjectLayouts />
        </TabPanel>
      </PageTabs>
    </>
  );
};

export default ObjectDetailsPage;
